
import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Row, Col, Space, Typography, Table, Select, Form, Input, message, Modal } from 'antd';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import DispatchCheckerService from '../../../services/Dispatch-Checker/DispatchCheckerService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DeleteOutlined, CheckCircleOutlined, QrcodeOutlined, PrinterOutlined, EditOutlined } from '@ant-design/icons';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { NotFoundException } from '@zxing/library';
import beepSound from '../../../assets/dashboard/scan-beep.mp3';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Text } = Typography;

const CapaReportPage = () => {
  const { state } = useLocation();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm(); // Form for edit modal
  const userData = JSON.parse(localStorage.getItem('userData'));
  const [selectedLine, setSelectedLine] = useState('');
  const [date, setDate] = useState(moment().startOf('day'));
  const [itemNo, setItemNo] = useState('');
  const [itemOptions, setItemOptions] = useState([]);  // ⬅️ new
  const [lineItems, setLineItems] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userName] = useState(userData?.data?.full_name || 'Unknown User');
  const [dueDate, setDueDate] = useState(moment().startOf('day').toDate());
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState('');
  const [reviewInfo, setReviewInfo] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false); // State for edit modal
  const [editRecord, setEditRecord] = useState(null); // State for record being edited
  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const beepSoundRef = useRef(new Audio(beepSound));
  const isScanProcessed = useRef(false);
  const [editDueDate, setEditDueDate] = useState(null);
  

  // Initial data load and cleanup
  useEffect(() => {
    console.log('Initial date set to:', date.format('YYYY-MM-DD'));
    if (userData?.data?.su_id) {
      fetchLinesAndItems(userData.data.su_id);
    }
    if (state?.line_no) {
      setSelectedLine(state.line_no);
    } else if (state?.item_no) {
      setItemNo(state.item_no);
    }
    return () => {
      stopScanning();
    };
  }, [state?.line_no, state?.item_no, userData?.data?.su_id]);

  // Fetch data when line, item, or date changes
  useEffect(() => {
    if (selectedLine && date && itemNo) {
      fetchCapaData();
    }
  }, [selectedLine, date, itemNo]);

  const handlePrint = () => {
  setLoading(true);
  try {
    const doc = new jsPDF('p', 'mm', 'a4');

    // === HEADER ===
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 48, 135);
    doc.text('Shahi Exports Pvt. Ltd., Knit Division - CAPA Report', 14, 15);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('Unit No.: 20', 14, 22);

    // === CUSTOMER INFO TABLE ===
    autoTable(doc, {
      body: [
        [`Customer: -`, `Item No.: ${itemNo || '-'}`, `P.O. No.: -`],
        [`Color: -`, `Style No.: -`, `Style Description: -`]
      ],
      theme: 'grid',
      styles: { fontSize: 11, cellPadding: 2 },
      startY: 28,
      margin: { left: 14, right: 14 },
      tableWidth: 'auto',
      bodyStyles: { fillColor: [249, 249, 249] },
    });

    const capaStartY = doc.lastAutoTable.finalY + 10;

    // === CAPA TABLE (Scaled to A4) ===
    autoTable(doc, {
      head: [[
        "Finding / Observation",
        "Root Cause",
        "Correction (Immediate)",
        "Corrective Action",
        "Prevention Plan",
        "Person Responsible",
        "Due Date",
        "Status"
      ]],
      body: tableData.map((item) => [
        item.finding_observation || '-',
        item.root_cause || '-',
        item.correction_Immediate || '-',
        item.corrective_action || '-',
        item.prevention_plan || '-',
        item.person_responsible || '-',
        item.due_date ? moment(item.due_date).format('DD-MM-YYYY') : '-',
        item.status || '-'
      ]),
      startY: capaStartY,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2, valign: 'middle', overflow: 'linebreak' },
      headStyles: { 
        fillColor: [0, 48, 135], 
        textColor: 255, 
        halign: 'center',
        fontStyle: 'bold'
      },
      bodyStyles: { halign: 'center' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 20, halign: 'left' },   // Finding
        1: { cellWidth: 20, halign: 'left' },   // Root cause
        2: { cellWidth: 23, halign: 'left' },   // Correction
        3: { cellWidth: 23, halign: 'left' },   // Corrective Action
        4: { cellWidth: 23, halign: 'left' },   // Prevention
        5: { cellWidth: 23, halign: 'left' },   // Person Responsible
        6: { cellWidth: 22, halign: 'center' }, // Due Date
        7: { cellWidth: 22, halign: 'center' }  // Status
      },
      tableWidth: 180, // Force fit within A4 width
      margin: { left: 14, right: 14 },
    });

    // === FOOTER OUTSIDE TABLE ===
    const finalY = doc.lastAutoTable.finalY + 10;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);

    // Left Footer
    doc.text(`Form No.: SQS/SEW/FM/048`, 14, finalY);
    doc.text(`Date: ${moment().format('DD-MM-YYYY')}`, 14, finalY + 5);

    // Right Footer
    const reviewerName = tableData[0]?.review_by || 'N/A';
    const reviewDate = tableData[0]?.review_dates?.[0]
      ? moment(tableData[0].review_dates[0]).format('DD-MM-YYYY')
      : 'N/A';

    doc.text(`Reviewed by: ${reviewerName}`, pageWidth - 70, finalY);
    doc.text(`Review Date: ${reviewDate}`, pageWidth - 70, finalY + 5);

    // Page Number
    const pageCount = doc.internal.getNumberOfPages();
    doc.text(
      `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );

    // === OPEN PDF ===
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');

  } catch (err) {
    console.error('Error generating PDF:', err);
    message.error('Failed to generate PDF');
  } finally {
    setLoading(false);
  }
};


  const fetchLinesAndItems = async (su_id) => {
    setLoading(true);
    try {
      const requestData = { su_id };
      console.log('get_userline_and_items Request:', requestData);
      const response = await DispatchCheckerService.get_userline_and_items(su_id);
      console.log('get_userline_and_items Response:', response);
      if (response.status && Array.isArray(response.line_data)) {
        setLineItems(response.line_data);
        if (response.line_data.length > 0 && !selectedLine) {
          setSelectedLine(response.line_data[0].line_no);
          const selectedLineData = response.line_data[0];
          setItemNo(selectedLineData?.items[0]?.item_no || '');
        }
      } else {
        console.warn('Invalid response format:', response);
        setLineItems([]);
        message.error(response.message || 'No lines or items available');
      }
    } catch (error) {
      console.error('Error fetching lines and items:', error);
      setLineItems([]);
      message.error('Failed to fetch lines and items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCapaData = async () => {
    if (!selectedLine || !date || !itemNo) return;
    setLoading(true);
    try {
      const requestData = {
        line_no: selectedLine,
        item_no: itemNo,
        added_on: date.format('YYYY-MM-DD'),
      };
      console.log('get_eol_capa_by_filter Request:', requestData);
      const response = await DispatchCheckerService.get_eol_capa_by_filter(selectedLine, itemNo, date.format('YYYY-MM-DD'));
      console.log('get_eol_capa_by_filter Response:', response);
      if (response.status) { 
        const capaData = response.data.map(item => ({
          key: item.ecr_id,
          ecr_id: item.ecr_id,
          finding_observation: item.finding_observation,
          root_cause: item.root_cause,
          correction_Immediate: item.correction_Immediate,
          corrective_action: item.corrective_action,
          prevention_plan: item.prevention_plan,
          person_responsible: item.person_responsible,
          due_date: item.due_date,
          status: item.status,
          review_by: item.review_by,
          review_dates: item.review_dates || [],
        }));
        setTableData(capaData);
        const reviewedItem = capaData.find(item => item.review_by && item.review_by.trim() !== '');
        if (reviewedItem && reviewedItem.review_dates.length > 0) {
          setReviewInfo({
            reviewer: reviewedItem.review_by,
            date: reviewedItem.review_dates[0],
          });
        } else {
          setReviewInfo(null);
        }
      } else {
        setTableData([]);
        message.error(response.message || 'Failed to fetch CAPA data');
        setReviewInfo(null);
      }
    } catch (error) {
      console.error('Error fetching CAPA data:', error);
      message.error('An error occurred while fetching CAPA data');
      setTableData([]);
      setReviewInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLineChange = (value) => {
    setSelectedLine(value);
    const selectedLineData = lineItems.find(line => line.line_no === value);
    setItemNo(selectedLineData?.items[0]?.item_no || '');
  };

  const handleDateChange = (date) => {
    setDate(date ? moment(date) : null);
    console.log('Date changed to:', date ? moment(date).format('YYYY-MM-DD') : 'null');
  };

 const handleDueDateChange = (date) => {
  setEditDueDate(date);
  editForm.setFieldsValue({ due_date: date }); // Sync form with state
};

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const capaData = {
        line_no: selectedLine,
        item_no: itemNo,
        finding_observation: values.finding_observation,
        root_cause: values.root_cause,
        correction_Immediate: values.correction_Immediate,
        corrective_action: values.corrective_action,
        prevention_plan: values.prevention_plan,
        person_responsible: values.person_responsible,
        due_date: values.due_date ? moment(values.due_date).format('YYYY-MM-DD') : '',
        status: values.status || 'Open',
      };
      console.log('insert_eol_capa_report Request:', capaData);
      const response = await DispatchCheckerService.insert_eol_capa_report(capaData);
      console.log('insert_eol_capa_report Response:', response);
      if (response.status) {
        message.success(response.message);
        form.resetFields();
        setDueDate(moment().add(1, 'month').toDate());
        fetchCapaData();
      } else {
        message.error(response.message || 'Failed to insert CAPA report');
      }
    } catch (error) {
      console.error('Error inserting CAPA report:', error);
      message.error('An error occurred while inserting CAPA report');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ecr_id) => {
  Modal.confirm({
    title: 'Confirm Delete',
    content: 'Are you sure you want to delete this CAPA Data?',
    okText: 'Yes',
    okType: 'danger',
    cancelText: 'No',
    onOk: async () => {
      setLoading(true);
      try {
        const requestData = { ecr_id };
        console.log('delete_eol_capa_report Request:', requestData);
        const response = await DispatchCheckerService.delete_eol_capa_report(ecr_id);
        console.log('delete_eol_capa_report Response:', response);
        if (response.status) {
          message.success(response.message);
          fetchCapaData();
        } else {
          message.error(response.message || 'Failed to delete CAPA report');
        }
      } catch (error) {
        console.error('Error deleting CAPA report:', error);
        message.error('An error occurred while deleting CAPA report');
      } finally {
        setLoading(false);
      }
    }
  });
};

 const handleEdit = (record) => {
  setEditRecord(record);
  setIsEditModalVisible(true);
  editForm.setFieldsValue({
    finding_observation: record.finding_observation,
    root_cause: record.root_cause,
    correction_Immediate: record.correction_Immediate,
    corrective_action: record.corrective_action,
    prevention_plan: record.prevention_plan,
    person_responsible: record.person_responsible,
    due_date: record.due_date ? moment(record.due_date).toDate() : null,
    status: record.status,
  });
  setEditDueDate(record.due_date ? moment(record.due_date).toDate() : null); // Sync state with initial value
};

  const handleEditSubmit = async (values) => {
    setLoading(true);
    try {
      const capaData = {
        ecr_id: editRecord.ecr_id,
        line_no: selectedLine,
        item_no: itemNo,
        finding_observation: values.finding_observation,
        root_cause: values.root_cause,
        correction_Immediate: values.correction_Immediate,
        corrective_action: values.corrective_action,
        prevention_plan: values.prevention_plan,
        person_responsible: values.person_responsible,
        due_date: values.due_date ? moment(values.due_date).format('YYYY-MM-DD') : '',
        status: values.status || 'Open',
      };
      console.log('update_eol_capa_report Request:', capaData);
      const response = await DispatchCheckerService.update_eol_capa_report(capaData);
      console.log('update_eol_capa_report Response:', response);
      if (response.status) {
        message.success(response.message || 'CAPA report updated successfully');
        setIsEditModalVisible(false);
        editForm.resetFields();
        setEditRecord(null);
        fetchCapaData();
      } else {
        message.error(response.message || 'Failed to update CAPA report');
      }
    } catch (error) {
      console.error('Error updating CAPA report:', error);
      message.error('An error occurred while updating CAPA report');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    editForm.resetFields();
    setEditRecord(null);
  };

  const startScanning = async () => {
    setIsScanning(true);
    setScanResult('');
    isScanProcessed.current = false;
    codeReader.current = new BrowserMultiFormatReader();
    try {
      const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices();
      if (videoInputDevices.length === 0) {
        message.error('No camera devices found');
        setIsScanning(false);
        codeReader.current = null;
        return;
      }
      const deviceId = videoInputDevices[0].deviceId;
      codeReader.current.decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
        if (result && !isScanProcessed.current) {
          isScanProcessed.current = true;
          setScanResult(result.getText());
          beepSoundRef.current.play().catch(error => console.error('Beep play error:', error));
          handleReview(result.getText());
          stopScanning();
        }
        if (err && !(err instanceof NotFoundException)) {
          console.error('Scanning error:', err);
          message.error('Error during scanning');
          stopScanning();
        }
      }, { once: true });
    } catch (error) {
      console.error('Error starting scanner:', error);
      message.error('Failed to start scanner');
      setIsScanning(false);
      codeReader.current = null;
    }
  };

  const stopScanning = () => {
    if (codeReader.current) {
      if (typeof codeReader.current.stop === 'function') {
        codeReader.current.stop();
      }
    }
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    codeReader.current = null;
    setIsScanning(false);
    setScanResult('');
  };

  const handleReview = async (barcode) => {
    setLoading(true);
    try {
      const ecrIds = tableData.map(item => item.ecr_id).join(',');
      if (!ecrIds) {
        message.error('No CAPA reports available to review');
        setLoading(false);
        return;
      }
      const reviewData = {
        line_no: selectedLine,
        item_no: itemNo,
        ecr_id: ecrIds,
        review_date: moment().format('YYYY-MM-DD HH:mm:ss'),
        review_by: barcode,
        added_by: userData?.data?.username || 'unknown',
      };
      console.log('store_eol_capa_reviews Request:', reviewData);
      const response = await DispatchCheckerService.store_eol_capa_reviews(reviewData);
      console.log('store_eol_capa_reviews Response:', response);
      if (response.status) {
        message.success('Review recorded successfully');
        fetchCapaData();
      } else {
        message.error(response.message || 'Failed to record review');
      }
    } catch (error) {
      console.error('Error recording review:', error);
      message.error('An error occurred while recording review');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Finding / Observation', dataIndex: 'finding_observation', key: 'finding_observation', width: '20%' },
    { title: 'Root Cause', dataIndex: 'root_cause', key: 'root_cause', width: '15%' },
    { title: 'Correction(Immediate)', dataIndex: 'correction_Immediate', key: 'correction_Immediate', width: '15%' },
    { title: 'Corrective Action', dataIndex: 'corrective_action', key: 'corrective_action', width: '15%' },
    { title: 'Prevention Plan', dataIndex: 'prevention_plan', key: 'prevention_plan', width: '15%' },
    { title: 'Person Responsible', dataIndex: 'person_responsible', key: 'person_responsible', width: '10%' },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: '10%',
      render: (text) => (text ? moment(text).format('YYYY-MM-DD') : ''),
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: '10%' },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            className="action-button edit-button"
          >
            
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.ecr_id)}
            size="small"
            className="action-button"
          >
            
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="capa-report-container">
      <Card className="main-card">
        <Row gutter={[16, 16]} className="filter-row">
          <Col xs={24} sm={12} md={8}>
            <Text strong>Line</Text>
            <Select
              style={{ width: '100%' }}
              value={selectedLine}
              onChange={handleLineChange}
              size="large"
              className="custom-select"
              bordered={false} // Remove default border
              suffixIcon={null} // Remove default dropdown icon if needed
            >
              {lineItems.map(line => (
                <Select.Option key={line.line_no} value={line.line_no}>
                  {line.line_no}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Item</Text>
            <Select
              style={{ width: '100%' }}
              value={itemNo}
              onChange={setItemNo}
              size="large"
              className="custom-select"
              bordered={false} // Remove default border
              suffixIcon={null} // Remove default dropdown icon if needed
            >
              {lineItems
                .find(line => line.line_no === selectedLine)
                ?.items.map(item => (
                  <Select.Option key={item.item_no} value={item.item_no}>
                    {item.item_no}
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Date</Text>
            <div style={{ width: '100%' }}>
              <DatePicker
                selected={date?.toDate()}
                onChange={handleDateChange}
                dateFormat="yyyy-MM-dd"
                todayButton="Today"
                placeholderText="Select date"
                className="custom-date-picker"
              />
            </div>
          </Col>
        </Row>

        <Card title="Add New" className="form-card">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              due_date: dueDate,
              status: 'Open',
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="finding_observation"
                  label="Finding / Observation"
                  rules={[{ required: true, message: 'Please enter finding observation' }]}
                >
                  <Input.TextArea
                    className="custom-textarea"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    placeholder="Enter finding or observation"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="root_cause"
                  label="Root Cause"
                  rules={[{ required: true, message: 'Please enter root cause' }]}
                >
                  <Input.TextArea
                    className="custom-textarea"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    placeholder="Enter root cause"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="correction_Immediate"
                  label="Correction (Immediate)"
                  rules={[{ required: true, message: 'Please enter correction immediate' }]}
                >
                  <Input.TextArea
                    className="custom-textarea"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    placeholder="Enter immediate correction"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="corrective_action"
                  label="Corrective Action"
                  rules={[{ required: true, message: 'Please enter corrective action' }]}
                >
                  <Input.TextArea
                    className="custom-textarea"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    placeholder="Enter corrective action"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="prevention_plan"
                  label="Prevention Plan"
                  rules={[{ required: true, message: 'Please enter prevention plan' }]}
                >
                  <Input.TextArea
                    className="custom-textarea"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    placeholder="Enter prevention plan"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="person_responsible"
                  label="Person Responsible"
                  rules={[{ required: true, message: 'Please enter person responsible' }]}
                >
                  <Input.TextArea
                    className="custom-textarea"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    placeholder="Enter person responsible"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="due_date"
                  label="Due Date"
                  rules={[{ required: true, message: 'Please select due date' }]}
                >
              <DatePicker
            selected={editDueDate}
            onChange={handleDueDateChange}
            dateFormat="yyyy-MM-dd"
            placeholderText="Select due date"
            className="custom-date-picker"
          />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select status' }]}
                >
                  <Select placeholder="Select status" className="custom-select">
                    <Select.Option value="Open">Open</Select.Option>
                    <Select.Option value="In Progress">In Progress</Select.Option>
                    <Select.Option value="Closed">Closed</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24} style={{ textAlign: 'center' }}>
                <Form.Item>
                  {tableData.length > 0 && tableData[0].review_by === 'vikas' ? null : (
                    <Button type="primary" htmlType="submit" loading={loading} className="submit-button">
                      Save
                    </Button>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          className="capa-table"
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={false}
          bordered
          scroll={{ x: 800 }}
          size="middle"
        />

      <Row justify="center" style={{ marginTop: '24px' }}>
  <Col>
    <Space>
      {reviewInfo ? (
        <span style={{ color: 'green' }}>
          <CheckCircleOutlined style={{ marginRight: '8px', color: 'green' }} />
          Reviewed by {reviewInfo.reviewer} on {reviewInfo.date}
        </span>
      ) : (
        <Button
          type="primary"
          icon={<QrcodeOutlined />}
          onClick={startScanning}
          loading={loading}
          className="review-button"
          disabled={isScanning}
        >
          {isScanning ? 'Scanning...' : 'Scan Review'}
        </Button>
      )}
      {isScanning && (
        <Button
          type="default"
          onClick={stopScanning}
          className="stop-button"
        >
          Stop Scan
        </Button>
      )}
    </Space>
  </Col>
</Row>
<Row justify="end" style={{ marginTop: '0px' }}>
  <Col>
    <Button
      type="primary"
      icon={<PrinterOutlined />}
      onClick={handlePrint}
      loading={loading}
      className="print-button"
    >
      Print
    </Button>
  </Col>
</Row>
      </Card>

      <Modal
        title="Scan Barcode"
        visible={isScanning}
        onCancel={stopScanning}
        footer={null}
        centered
        className="scan-modal"
      >
        <div className="video-container">
          <video ref={videoRef} className="video-feed" />
          {scanResult && <Text className="scan-result">Scanned: {scanResult}</Text>}
        </div>
      </Modal>

      <Modal
        title="Edit CAPA Report"
        visible={isEditModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        centered
        className="edit-modal"
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="finding_observation"
                label="Finding / Observation"
                rules={[{ required: true, message: 'Please enter finding observation' }]}
              >
                <Input.TextArea
                  className="custom-textarea"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder="Enter finding or observation"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="root_cause"
                label="Root Cause"
                rules={[{ required: true, message: 'Please enter root cause' }]}
              >
                <Input.TextArea
                  className="custom-textarea"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder="Enter root cause"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="correction_Immediate"
                label="Correction (Immediate)"
                rules={[{ required: true, message: 'Please enter correction immediate' }]}
              >
                <Input.TextArea
                  className="custom-textarea"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder="Enter immediate correction"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="corrective_action"
                label="Corrective Action"
                rules={[{ required: true, message: 'Please enter corrective action' }]}
              >
                <Input.TextArea
                  className="custom-textarea"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder="Enter corrective action"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="prevention_plan"
                label="Prevention Plan"
                rules={[{ required: true, message: 'Please enter prevention plan' }]}
              >
                <Input.TextArea
                  className="custom-textarea"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder="Enter prevention plan"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="person_responsible"
                label="Person Responsible"
                rules={[{ required: true, message: 'Please enter person responsible' }]}
              >
                <Input.TextArea
                  className="custom-textarea"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder="Enter person responsible"
                />
              </Form.Item>
            </Col>
           <Col xs={24} sm={12}>
        <Form.Item
          name="due_date"
          label="Due Date"
          rules={[{ required: true, message: 'Please select due date' }]}
        >
          <DatePicker
            selected={editDueDate}
            onChange={handleDueDateChange}
            dateFormat="yyyy-MM-dd"
            placeholderText="Select due date"
            className="custom-date-picker"
          />
        </Form.Item>
      </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status" className="custom-select">
                  <Select.Option value="Open">Open</Select.Option>
                  <Select.Option value="In Progress">In Progress</Select.Option>
                  <Select.Option value="Closed">Closed</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24} style={{ textAlign: 'center' }}>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} className="submit-button">
                  Update
                </Button>
                <Button type="default" onClick={handleEditCancel} style={{ marginLeft: 8 }}>
                  Cancel
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <style>
        {`
          .custom-textarea {
            border-radius: 6px;
            border: 1px solid #d9d9d9;
            font-size: 14px;
            transition: all 0.3s;
            resize: none;
          }
          .custom-textarea:focus {
            border-color: #1d39c4;
            box-shadow: 0 0 0 2px rgba(29, 57, 196, 0.2);
          }
          @media (max-width: 768px) {
            .custom-textarea {
              font-size: 13px;
            }
          }
          @media (max-width: 576px) {
            .custom-textarea {
              font-size: 12px;
            }
          }
          // .capa-report-container {
          //   padding: 24px;
          //   background-color: #000000FF;
          //   min-height: 100vh;
          // }
          .main-card {
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            background: #ffffff;
            padding: 24px;
          }
          .filter-row {
            margin-bottom: 24px;
          }
          .form-card {
            border-radius: 8px;
            margin-bottom: 24px;
            background: #fafafa;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          }
          .form-card .ant-card-head {
            background: #1d39c4;
            color: #ffffff;
            border-radius: 6px 6px 0 0;
            font-weight: 500;
          }
          .custom-select {
            border-radius: 6px;
            border: 1px solid #d9d9d9;
            height: 40px;
            font-size: 14px;
            transition: all 0.3s;
            background: #ffffff; /* Ensure background matches input */
          }
          .custom-select:focus {
            border-color: #1d39c4;
            box-shadow: 0 0 0 2px rgba(29, 57, 196, 0.2);
          }
          .custom-date-picker {
            border-radius: 6px;
            border: 1px solid #d9d9d9;
            height: 40px;
            font-size: 14px;
            transition: all 0.3s;
            width: 100%;
            padding: 8px 12px;
            box-sizing: border-box;
          }
          .custom-date-picker:focus {
            border-color: #1d39c4;
            box-shadow: 0 0 0 2px rgba(29, 57, 196, 0.2);
          }
          .react-datepicker {
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            border-radius: 6px;
          }
          .react-datepicker__day--selected {
            background-color: #1d39c4 !important;
            color: #ffffff !important;
          }
          .react-datepicker__day--keyboard-selected {
            background-color: #597ef7 !important;
          }
          .submit-button {
            border-radius: 6px;
            padding: 8px 24px;
            font-size: 14px;
            background: #1d39c4;
            border: none;
            transition: all 0.3s;
          }
          .submit-button:hover {
            background: #597ef7;
          }
          .capa-table {
            border-radius: 8px;
            overflow: hidden;
            background: #ffffff;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          }
          .capa-table .ant-table-thead > tr > th {
            background: #f0f5ff;
            color: #1d39c4;
            font-weight: 500;
            white-space: nowrap;
          }
          .capa-table .ant-table-tbody > tr > td {
            white-space: nowrap;
            font-size: 13px;
          }
          .action-button {
            border-radius: 4px;
            font-size: 12px;
            padding: 4px 12px;
          }
          .edit-button {
            background: #faad14;
            border: none;
          }
          .edit-button:hover {
            background: #ffc107;
          }
          .review-button {
            border-radius: 6px;
            padding: 8px 24px;
            font-size: 14px;
            background: #52c41a;
            border: none;
            transition: all 0.3s;
          }
          .review-button:hover {
            background: #73d13d;
          }
          .stop-button {
            border-radius: 6px;
            padding: 8px 24px;
            font-size: 14px;
            transition: all 0.3s;
          }
          .scan-modal .ant-modal-content, .edit-modal .ant-modal-content {
            border-radius: 8px;
          }
          .video-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }
          .video-feed {
            width: 100%;
            max-width: 400px;
            height: 300px;
            border-radius: 6px;
            border: 1px solid #d9d9d9;
          }
          .scan-result {
            font-size: 14px;
            color: #1d39c4;
            font-weight: 500;
          }
          .footer-row {
            margin-top: 24px;
          }
          @media (max-width: 768px) {
            .capa-report-container {
              padding: 16px;
            }
            .main-card {
              padding: 16px;
            }
            .custom-select, .custom-date-picker {
              height: 36px;
              font-size: 13px;
            }
            .submit-button, .review-button, .stop-button {
              padding: 6px 16px;
              font-size: 13px;
            }
            .capa-table {
              font-size: 12px;
            }
            .capa-table .ant-table-thead > tr > th,
            .capa-table .ant-table-tbody > tr > td {
              padding: 8px 6px;
            }
            .action-button {
              padding: 4px 8px;
              font-size: 11px;
            }
            .video-feed {
              max-width: 300px;
              height: 200px;
            }
          }
          @media (max-width: 576px) {
            .capa-report-container {
              padding: 12px;
            }
            .custom-select, .custom-date-picker {
              height: 32px;
              font-size: 12px;
            }
            .submit-button, .review-button, .stop-button {
              padding: 4px 12px;
              font-size: 12px;
            }
            .capa-table {
              font-size: 11px;
            }
            .video-feed {
              max-width: 250px;
              height: 150px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CapaReportPage;

// import React, { useState, useEffect, useRef } from 'react';
// import { Button, Card, Row, Col, Space, Typography, Table, Select, Form, Input, message, Modal } from 'antd';
// import { useLocation } from 'react-router-dom';
// import moment from 'moment';
// import { DeleteOutlined, CheckCircleOutlined, QrcodeOutlined, PrinterOutlined, EditOutlined } from '@ant-design/icons';
// import { BrowserMultiFormatReader } from '@zxing/browser';
// import { NotFoundException } from '@zxing/library';
// import DatePicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
// import beepSound from '../../../assets/dashboard/scan-beep.mp3';
// import html2canvas from 'html2canvas';
// import jsPDF from 'jspdf';
// import autoTable from 'jspdf-autotable';
// import DispatchCheckerService from '../../../services/Dispatch-Checker/DispatchCheckerService';

// const { Text } = Typography;

// const CapaReportPage = () => {
//   const { state } = useLocation();
//   const [form] = Form.useForm();
//   const [editForm] = Form.useForm();
//   const userData = JSON.parse(localStorage.getItem('userData'));
//   const [selectedLine, setSelectedLine] = useState('');
//   const [date, setDate] = useState(moment().startOf('day'));
//   const [itemNo, setItemNo] = useState('');
//   const [lineItems, setLineItems] = useState([]);
//   const [itemOptions, setItemOptions] = useState([]);
//   const [tableData, setTableData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [userName] = useState(userData?.data?.full_name || 'Unknown User');
//   const [dueDate, setDueDate] = useState(moment().startOf('day').toDate());
//   const [isScanning, setIsScanning] = useState(false);
//   const [scanResult, setScanResult] = useState('');
//   const [reviewInfo, setReviewInfo] = useState(null);
//   const [isEditModalVisible, setIsEditModalVisible] = useState(false);
//   const [editRecord, setEditRecord] = useState(null);
//   const [editDueDate, setEditDueDate] = useState(null);
// const [siteTitle, setSiteTitle] = useState(''); 
//  const videoRef = useRef(null);
//   const codeReader = useRef(null);
//   const beepSoundRef = useRef(new Audio(beepSound));
//   const isScanProcessed = useRef(false);
//   const [unitNo, setUnitNo] = useState('');

//   // Initial data load and cleanup
//   useEffect(() => {
//     console.log('Initial date set to:', date.format('YYYY-MM-DD'));
//     if (userData?.data?.su_id) {
//       fetchLinesAndItems(userData.data.su_id);
//     }
//     if (state?.line_no) {
//       setSelectedLine(state.line_no);
//     } else if (state?.item_no) {
//       setItemNo(state.item_no);
//     }
//     fetchSiteTitle();
//     return () => {
//       stopScanning();
//     };
//   }, [state?.line_no, state?.item_no, userData?.data?.su_id]);

//   // Fetch items when line changes
//   useEffect(() => {
//     if (selectedLine) {
//       fetchItemsForCapa(selectedLine);
//     }
//   }, [selectedLine]);

//   // Fetch CAPA data when line, item, or date changes
//   useEffect(() => {
//     if (selectedLine && date && itemNo) {
//       fetchCapaData();
//     }
//   }, [selectedLine, date, itemNo]);

// const fetchSiteTitle = async () => {
//   try {
//     const response = await DispatchCheckerService.getSiteTitle();
//     if (response.status && response.title) {
//       setSiteTitle(response.title);
//       // Extract unit number (e.g., "UNIT-11") from title
//       const unitMatch = response.title.match(/UNIT-\d+/);
//       setUnitNo(unitMatch ? unitMatch[0] : 'Unknown');
//     } else {
//       console.warn('Invalid site title response:', response);
//       message.error(response.message || 'Failed to fetch site title');
//     }
//   } catch (error) {
//     console.error('Error fetching site title:', error);
//     message.error('Failed to fetch site title');
//   }
// };

// const fetchLinesAndItems = async (su_id) => {
//     setLoading(true);
//     try {
//       const response = await DispatchCheckerService.get_userline_and_items(su_id);
//       console.log('get_userline_and_items Response:', response);
//       if (response.status && Array.isArray(response.line_data)) {
//         setLineItems(response.line_data);
//         if (response.line_data.length > 0 && !selectedLine) {
//           setSelectedLine(response.line_data[0].line_no);
//         }
//       } else {
//         console.warn('Invalid response format:', response);
//         setLineItems([]);
//         message.error(response.message || 'No lines or items available');
//       }
//     } catch (error) {
//       console.error('Error fetching lines and items:', error);
//       setLineItems([]);
//       message.error('Failed to fetch lines and items');
//     } finally {
//       setLoading(false);
//     }
//   };
//   const fetchItemsForCapa = async (lineNo) => {
//     setLoading(true);
//     try {
//       const response = await DispatchCheckerService.getAllItemsForCapa(lineNo);
//       console.log('get_all_item_for_capa Response:', response);
//       if (response.status && Array.isArray(response.data)) {
//         setItemOptions(response.data);
//         if (response.data.length > 0 && !itemNo) {
//           setItemNo(response.data[0].item_no);
//         }
//       } else {
//         console.warn('Invalid items response:', response);
//         setItemOptions([]);
//         message.error(response.message || 'No items available');
//       }
//     } catch (error) {
//       console.error('Error fetching items for CAPA:', error);
//       setItemOptions([]);
//       message.error('Failed to fetch items for CAPA');
//     } finally {
//       setLoading(false);
//     }}

//   const fetchCapaData = async () => {
//     if (!selectedLine || !date || !itemNo) return;
//     setLoading(true);
//     try {
//       const requestData = {
//         line_no: selectedLine,
//         item_no: itemNo,
//         added_on: date.format('YYYY-MM-DD'),
//       };
//       console.log('get_eol_capa_by_filter Request:', requestData);
//       const response = await DispatchCheckerService.get_eol_capa_by_filter(selectedLine, itemNo, date.format('YYYY-MM-DD'));
//       console.log('get_eol_capa_by_filter Response:', response);
//       if (response.status) {
//         const capaData = response.data.map(item => ({
//           key: item.ecr_id,
//           ecr_id: item.ecr_id,
//           finding_observation: item.finding_observation,
//           root_cause: item.root_cause,
//           correction_Immediate: item.correction_Immediate,
//           corrective_action: item.corrective_action,
//           prevention_plan: item.prevention_plan,
//           person_responsible: item.person_responsible,
//           due_date: item.due_date,
//           status: item.status,
//           review_by: item.review_by,
//           review_dates: item.review_dates || [],
//         }));
//         setTableData(capaData);
//         const reviewedItem = capaData.find(item => item.review_by && item.review_by.trim() !== '');
//         if (reviewedItem && reviewedItem.review_dates.length > 0) {
//           setReviewInfo({
//             reviewer: reviewedItem.review_by,
//             date: reviewedItem.review_dates[0],
//           });
//         } else {
//           setReviewInfo(null);
//         }
//       } else {
//         setTableData([]);
//         message.error(response.message || 'Failed to fetch CAPA data');
//         setReviewInfo(null);
//       }
//     } catch (error) {
//       console.error('Error fetching CAPA data:', error);
//       message.error('An error occurred while fetching CAPA data');
//       setTableData([]);
//       setReviewInfo(null);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handlePrint = () => {
//     setLoading(true);
//     try {
//       const doc = new jsPDF('p', 'mm', 'a4');

//      doc.setFontSize(16);
//     doc.setFont('helvetica', 'bold');
//     doc.setTextColor(0, 48, 135);
//     doc.text(`${siteTitle} - CAPA Report`, 14, 15);

//     // doc.setFontSize(12);
//     // doc.setFont('helvetica', 'normal');
//     // doc.setTextColor(60, 60, 60);
//     // doc.text(`Unit No.: ${unitNo.replace('UNIT-', '')}`, 14, 22);
//       // === CUSTOMER INFO TABLE ===
//       autoTable(doc, {
//         body: [
//           [`Customer: -`, `Item No.: ${itemNo || '-'}`, `P.O. No.: -`],
//           [`Color: -`, `Style No.: -`, `Style Description: -`],
//         ],
//         theme: 'grid',
//         styles: { fontSize: 11, cellPadding: 2 },
//         startY: 28,
//         margin: { left: 14, right: 14 },
//         tableWidth: 'auto',
//         bodyStyles: { fillColor: [249, 249, 249] },
//       });

//       const capaStartY = doc.lastAutoTable.finalY + 10;

//       // === CAPA TABLE ===
//       autoTable(doc, {
//         head: [
//           [
//             'Finding / Observation',
//             'Root Cause',
//             'Correction (Immediate)',
//             'Corrective Action',
//             'Prevention Plan',
//             'Person Responsible',
//             'Due Date',
//             'Status',
//           ],
//         ],
//         body: tableData.map(item => [
//           item.finding_observation || '-',
//           item.root_cause || '-',
//           item.correction_Immediate || '-',
//           item.corrective_action || '-',
//           item.prevention_plan || '-',
//           item.person_responsible || '-',
//           item.due_date ? moment(item.due_date).format('DD-MM-YYYY') : '-',
//           item.status || '-',
//         ]),
//         startY: capaStartY,
//         theme: 'grid',
//         styles: { fontSize: 9, cellPadding: 2, valign: 'middle', overflow: 'linebreak' },
//         headStyles: {
//           fillColor: [0, 48, 135],
//           textColor: 255,
//           halign: 'center',
//           fontStyle: 'bold',
//         },
//         bodyStyles: { halign: 'center' },
//         alternateRowStyles: { fillColor: [245, 245, 245] },
//         columnStyles: {
//           0: { cellWidth: 20, halign: 'left' },
//           1: { cellWidth: 20, halign: 'left' },
//           2: { cellWidth: 23, halign: 'left' },
//           3: { cellWidth: 23, halign: 'left' },
//           4: { cellWidth: 23, halign: 'left' },
//           5: { cellWidth: 23, halign: 'left' },
//           6: { cellWidth: 22, halign: 'center' },
//           7: { cellWidth: 22, halign: 'center' },
//         },
//         tableWidth: 180,
//         margin: { left: 14, right: 14 },
//       });

//       // === FOOTER ===
//       const finalY = doc.lastAutoTable.finalY + 10;
//       const pageWidth = doc.internal.pageSize.width;
//       const pageHeight = doc.internal.pageSize.height;

//       doc.setFontSize(10);
//       doc.setTextColor(80, 80, 80);

//       // Left Footer
//       doc.text(`Form No.: SQS/SEW/FM/048`, 14, finalY);
//       doc.text(`Date: ${moment().format('DD-MM-YYYY')}`, 14, finalY + 5);

//       // Right Footer
//       const reviewerName = tableData[0]?.review_by || 'N/A';
//       const reviewDate = tableData[0]?.review_dates?.[0]
//         ? moment(tableData[0].review_dates[0]).format('DD-MM-YYYY')
//         : 'N/A';

//       doc.text(`Reviewed by: ${reviewerName}`, pageWidth - 70, finalY);
//       doc.text(`Review Date: ${reviewDate}`, pageWidth - 70, finalY + 5);

//       // Page Number
//       const pageCount = doc.internal.getNumberOfPages();
//       doc.text(
//         `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
//         pageWidth / 2,
//         pageHeight - 10,
//         { align: 'center' }
//       );

//       // === OPEN PDF ===
//       const pdfBlob = doc.output('blob');
//       const pdfUrl = URL.createObjectURL(pdfBlob);
//       window.open(pdfUrl, '_blank');
//     } catch (err) {
//       console.error('Error generating PDF:', err);
//       message.error('Failed to generate PDF');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleLineChange = value => {
//     setSelectedLine(value);
//     setItemNo(''); // Reset item number when line changes
//   };

//   const handleDateChange = date => {
//     setDate(date ? moment(date) : null);
//     console.log('Date changed to:', date ? moment(date).format('YYYY-MM-DD') : 'null');
//   };

//   const handleDueDateChange = date => {
//     setEditDueDate(date);
//     editForm.setFieldsValue({ due_date: date });
//   };

//   const handleSubmit = async values => {
//     setLoading(true);
//     try {
//       const capaData = {
//         line_no: selectedLine,
//         item_no: itemNo,
//         finding_observation: values.finding_observation,
//         root_cause: values.root_cause,
//         correction_Immediate: values.correction_Immediate,
//         corrective_action: values.corrective_action,
//         prevention_plan: values.prevention_plan,
//         person_responsible: values.person_responsible,
//         due_date: values.due_date ? moment(values.due_date).format('YYYY-MM-DD') : '',
//         status: values.status || 'Open',
//       };
//       console.log('insert_eol_capa_report Request:', capaData);
//       const response = await DispatchCheckerService.insert_eol_capa_report(capaData);
//       console.log('insert_eol_capa_report Response:', response);
//       if (response.status) {
//         message.success(response.message);
//         form.resetFields();
//         setDueDate(moment().add(1, 'month').toDate());
//         fetchCapaData();
//       } else {
//         message.error(response.message || 'Failed to insert CAPA report');
//       }
//     } catch (error) {
//       console.error('Error inserting CAPA report:', error);
//       message.error('An error occurred while inserting CAPA report');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDelete = async ecr_id => {
//     Modal.confirm({
//       title: 'Confirm Delete',
//       content: 'Are you sure you want to delete this CAPA Data?',
//       okText: 'Yes',
//       okType: 'danger',
//       cancelText: 'No',
//       onOk: async () => {
//         setLoading(true);
//         try {
//           const requestData = { ecr_id };
//           console.log('delete_eol_capa_report Request:', requestData);
//           const response = await DispatchCheckerService.delete_eol_capa_report(ecr_id);
//           console.log('delete_eol_capa_report Response:', response);
//           if (response.status) {
//             message.success(response.message);
//             fetchCapaData();
//           } else {
//             message.error(response.message || 'Failed to delete CAPA report');
//           }
//         } catch (error) {
//           console.error('Error deleting CAPA report:', error);
//           message.error('An error occurred while deleting CAPA report');
//         } finally {
//           setLoading(false);
//         }
//       },
//     });
//   };

//   const handleEdit = record => {
//     setEditRecord(record);
//     setIsEditModalVisible(true);
//     editForm.setFieldsValue({
//       finding_observation: record.finding_observation,
//       root_cause: record.root_cause,
//       correction_Immediate: record.correction_Immediate,
//       corrective_action: record.corrective_action,
//       prevention_plan: record.prevention_plan,
//       person_responsible: record.person_responsible,
//       due_date: record.due_date ? moment(record.due_date).toDate() : null,
//       status: record.status,
//     });
//     setEditDueDate(record.due_date ? moment(record.due_date).toDate() : null);
//   };

//   const handleEditSubmit = async values => {
//     setLoading(true);
//     try {
//       const capaData = {
//         ecr_id: editRecord.ecr_id,
//         line_no: selectedLine,
//         item_no: itemNo,
//         finding_observation: values.finding_observation,
//         root_cause: values.root_cause,
//         correction_Immediate: values.correction_Immediate,
//         corrective_action: values.corrective_action,
//         prevention_plan: values.prevention_plan,
//         person_responsible: values.person_responsible,
//         due_date: values.due_date ? moment(values.due_date).format('YYYY-MM-DD') : '',
//         status: values.status || 'Open',
//       };
//       console.log('update_eol_capa_report Request:', capaData);
//       const response = await DispatchCheckerService.update_eol_capa_report(capaData);
//       console.log('update_eol_capa_report Response:', response);
//       if (response.status) {
//         message.success(response.message || 'CAPA report updated successfully');
//         setIsEditModalVisible(false);
//         editForm.resetFields();
//         setEditRecord(null);
//         fetchCapaData();
//       } else {
//         message.error(response.message || 'Failed to update CAPA report');
//       }
//     } catch (error) {
//       console.error('Error updating CAPA report:', error);
//       message.error('An error occurred while updating CAPA report');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEditCancel = () => {
//     setIsEditModalVisible(false);
//     editForm.resetFields();
//     setEditRecord(null);
//   };

//   const startScanning = async () => {
//     setIsScanning(true);
//     setScanResult('');
//     isScanProcessed.current = false;
//     codeReader.current = new BrowserMultiFormatReader();
//     try {
//       const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices();
//       if (videoInputDevices.length === 0) {
//         message.error('No camera devices found');
//         setIsScanning(false);
//         codeReader.current = null;
//         return;
//       }
//       const deviceId = videoInputDevices[0].deviceId;
//       codeReader.current.decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
//         if (result && !isScanProcessed.current) {
//           isScanProcessed.current = true;
//           setScanResult(result.getText());
//           beepSoundRef.current.play().catch(error => console.error('Beep play error:', error));
//           handleReview(result.getText());
//           stopScanning();
//         }
//         if (err && !(err instanceof NotFoundException)) {
//           console.error('Scanning error:', err);
//           message.error('Error during scanning');
//           stopScanning();
//         }
//       });
//     } catch (error) {
//       console.error('Error starting scanner:', error);
//       message.error('Failed to start scanner');
//       setIsScanning(false);
//       codeReader.current = null;
//     }
//   };

//   const stopScanning = () => {
//     if (codeReader.current) {
//       if (typeof codeReader.current.stop === 'function') {
//         codeReader.current.stop();
//       }
//     }
//     if (videoRef.current && videoRef.current.srcObject) {
//       const stream = videoRef.current.srcObject;
//       const tracks = stream.getTracks();
//       tracks.forEach(track => track.stop());
//       videoRef.current.srcObject = null;
//     }
//     codeReader.current = null;
//     setIsScanning(false);
//     setScanResult('');
//   };

//   const handleReview = async barcode => {
//     setLoading(true);
//     try {
//       const ecrIds = tableData.map(item => item.ecr_id).join(',');
//       if (!ecrIds) {
//         message.error('No CAPA reports available to review');
//         setLoading(false);
//         return;
//       }
//       const reviewData = {
//         line_no: selectedLine,
//         item_no: itemNo,
//         ecr_id: ecrIds,
//         review_date: moment().format('YYYY-MM-DD HH:mm:ss'),
//         review_by: barcode,
//         added_by: userData?.data?.username || 'unknown',
//       };
//       console.log('store_eol_capa_reviews Request:', reviewData);
//       const response = await DispatchCheckerService.store_eol_capa_reviews(reviewData);
//       console.log('store_eol_capa_reviews Response:', response);
//       if (response.status) {
//         message.success('Review recorded successfully');
//         fetchCapaData();
//       } else {
//         message.error(response.message || 'Failed to record review');
//       }
//     } catch (error) {
//       console.error('Error recording review:', error);
//       message.error('An error occurred while recording review');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const columns = [
//     { title: 'Finding / Observation', dataIndex: 'finding_observation', key: 'finding_observation', width: '20%' },
//     { title: 'Root Cause', dataIndex: 'root_cause', key: 'root_cause', width: '15%' },
//     { title: 'Correction(Immediate)', dataIndex: 'correction_Immediate', key: 'correction_Immediate', width: '15%' },
//     { title: 'Corrective Action', dataIndex: 'corrective_action', key: 'corrective_action', width: '15%' },
//     { title: 'Prevention Plan', dataIndex: 'prevention_plan', key: 'prevention_plan', width: '15%' },
//     { title: 'Person Responsible', dataIndex: 'person_responsible', key: 'person_responsible', width: '10%' },
//     {
//       title: 'Due Date',
//       dataIndex: 'due_date',
//       key: 'due_date',
//       width: '10%',
//       render: text => (text ? moment(text).format('YYYY-MM-DD') : ''),
//     },
//     { title: 'Status', dataIndex: 'status', key: 'status', width: '10%' },
//     {
//       title: 'Action',
//       key: 'action',
//       width: '10%',
//       render: (_, record) => (
//         <Space>
//           <Button
//             type="primary"
//             icon={<EditOutlined />}
//             onClick={() => handleEdit(record)}
//             size="small"
//             className="action-button edit-button"
//           />
//           <Button
//             type="primary"
//             danger
//             icon={<DeleteOutlined />}
//             onClick={() => handleDelete(record.ecr_id)}
//             size="small"
//             className="action-button"
//           />
//         </Space>
//       ),
//     },
//   ];

//   return (
//     <div className="capa-report-container">
//       <Card className="main-card">
//         <Row gutter={[16, 16]} className="filter-row">
//           <Col xs={24} sm={12} md={8}>
//             <Text strong>Line</Text>
//             <Select
//               style={{ width: '100%' }}
//               value={selectedLine}
//               onChange={handleLineChange}
//               size="large"
//               className="custom-select"
//               bordered={false}
//               suffixIcon={null}
//             >
//               {lineItems.map(line => (
//                 <Select.Option key={line.line_no} value={line.line_no}>
//                   {line.line_no}
//                 </Select.Option>
//               ))}
//             </Select>
//           </Col>
//           <Col xs={24} sm={12} md={8}>
//             <Text strong>Item</Text>
//             <Select
//               style={{ width: '100%' }}
//               value={itemNo}
//               onChange={setItemNo}
//               size="large"
//               className="custom-select"
//               bordered={false}
//               suffixIcon={null}
//             >
//               {itemOptions.map(item => (
//                 <Select.Option key={item.item_no} value={item.item_no}>
//                   {item.item_no}
//                 </Select.Option>
//               ))}
//             </Select>
//           </Col>
//           <Col xs={24} sm={12} md={8}>
//             <Text strong>Date</Text>
//             <div style={{ width: '100%' }}>
//               <DatePicker
//                 selected={date?.toDate()}
//                 onChange={handleDateChange}
//                 dateFormat="yyyy-MM-dd"
//                 todayButton="Today"
//                 placeholderText="Select date"
//                 className="custom-date-picker"
//               />
//             </div>
//           </Col>
//         </Row>

//         <Card title="Add New" className="form-card">
//           <Form
//             form={form}
//             layout="vertical"
//             onFinish={handleSubmit}
//             initialValues={{
//               due_date: dueDate,
//               status: 'Open',
//             }}
//           >
//             <Row gutter={[16, 16]}>
//               <Col xs={24} sm={12}>
//                 <Form.Item
//                   name="finding_observation"
//                   label="Finding / Observation"
//                   rules={[{ required: true, message: 'Please enter finding observation' }]}
//                 >
//                   <Input.TextArea
//                     className="custom-textarea"
//                     autoSize={{ minRows: 3, maxRows: 6 }}
//                     placeholder="Enter finding or observation"
//                   />
//                 </Form.Item>
//               </Col>
//               <Col xs={24} sm={12}>
//                 <Form.Item
//                   name="root_cause"
//                   label="Root Cause"
//                   rules={[{ required: true, message: 'Please enter root cause' }]}
//                 >
//                   <Input.TextArea
//                     className="custom-textarea"
//                     autoSize={{ minRows: 3, maxRows: 6 }}
//                     placeholder="Enter root cause"
//                   />
//                 </Form.Item>
//               </Col>
//               <Col xs={24} sm={12}>
//                 <Form.Item
//                   name="correction_Immediate"
//                   label="Correction (Immediate)"
//                   rules={[{ required: true, message: 'Please enter correction immediate' }]}
//                 >
//                   <Input.TextArea
//                     className="custom-textarea"
//                     autoSize={{ minRows: 3, maxRows: 6 }}
//                     placeholder="Enter immediate correction"
//                   />
//                 </Form.Item>
//               </Col>
//               <Col xs={24} sm={12}>
//                 <Form.Item
//                   name="corrective_action"
//                   label="Corrective Action"
//                   rules={[{ required: true, message: 'Please enter corrective action' }]}
//                 >
//                   <Input.TextArea
//                     className="custom-textarea"
//                     autoSize={{ minRows: 3, maxRows: 6 }}
//                     placeholder="Enter corrective action"
//                   />
//                 </Form.Item>
//               </Col>
//               <Col xs={24} sm={12}>
//                 <Form.Item
//                   name="prevention_plan"
//                   label="Prevention Plan"
//                   rules={[{ required: true, message: 'Please enter prevention plan' }]}
//                 >
//                   <Input.TextArea
//                     className="custom-textarea"
//                     autoSize={{ minRows: 3, maxRows: 6 }}
//                     placeholder="Enter prevention plan"
//                   />
//                 </Form.Item>
//               </Col>
//               <Col xs={24} sm={12}>
//                 <Form.Item
//                   name="person_responsible"
//                   label="Person Responsible"
//                   rules={[{ required: true, message: 'Please enter person responsible' }]}
//                 >
//                   <Input.TextArea
//                     className="custom-textarea"
//                     autoSize={{ minRows: 3, maxRows: 6 }}
//                     placeholder="Enter person responsible"
//                   />
//                 </Form.Item>
//               </Col>
//               <Col xs={24} sm={12}>
//                 <Form.Item
//                   name="due_date"
//                   label="Due Date"
//                   rules={[{ required: true, message: 'Please select due date' }]}
//                 >
//                   <DatePicker
//                     selected={dueDate}
//                     onChange={date => setDueDate(date)}
//                     dateFormat="yyyy-MM-dd"
//                     placeholderText="Select due date"
//                     className="custom-date-picker"
//                   />
//                 </Form.Item>
//               </Col>
//               <Col xs={24} sm={12}>
//                 <Form.Item
//                   name="status"
//                   label="Status"
//                   rules={[{ required: true, message: 'Please select status' }]}
//                 >
//                   <Select placeholder="Select status" className="custom-select">
//                     <Select.Option value="Open">Open</Select.Option>
//                     <Select.Option value="In Progress">In Progress</Select.Option>
//                     <Select.Option value="Closed">Closed</Select.Option>
//                   </Select>
//                 </Form.Item>
//               </Col>
//               <Col span={24} style={{ textAlign: 'center' }}>
//                 <Form.Item>
//                   {tableData.length > 0 && tableData[0].review_by === 'vikas' ? null : (
//                     <Button type="primary" htmlType="submit" loading={loading} className="submit-button">
//                       Save
//                     </Button>
//                   )}
//                 </Form.Item>
//               </Col>
//             </Row>
//           </Form>
//         </Card>

//         <Table
//           className="capa-table"
//           columns={columns}
//           dataSource={tableData}
//           loading={loading}
//           pagination={false}
//           bordered
//           scroll={{ x: 800 }}
//           size="middle"
//         />

//         <Row justify="center" style={{ marginTop: '24px' }}>
//           <Col>
//             <Space>
//               {reviewInfo ? (
//                 <span style={{ color: 'green' }}>
//                   <CheckCircleOutlined style={{ marginRight: '8px', color: 'green' }} />
//                   Reviewed by {reviewInfo.reviewer} on {reviewInfo.date}
//                 </span>
//               ) : (
//                 <Button
//                   type="primary"
//                   icon={<QrcodeOutlined />}
//                   onClick={startScanning}
//                   loading={loading}
//                   className="review-button"
//                   disabled={isScanning}
//                 >
//                   {isScanning ? 'Scanning...' : 'Scan Review'}
//                 </Button>
//               )}
//               {isScanning && (
//                 <Button type="default" onClick={stopScanning} className="stop-button">
//                   Stop Scan
//                 </Button>
//               )}
//             </Space>
//           </Col>
//         </Row>
//         <Row justify="end" style={{ marginTop: '0px' }}>
//           <Col>
//             <Button
//               type="primary"
//               icon={<PrinterOutlined />}
//               onClick={handlePrint}
//               loading={loading}
//               className="print-button"
//             >
//               Print
//             </Button>
//           </Col>
//         </Row>
//       </Card>

//       <Modal
//         title="Scan Barcode"
//         visible={isScanning}
//         onCancel={stopScanning}
//         footer={null}
//         centered
//         className="scan-modal"
//       >
//         <div className="video-container">
//           <video ref={videoRef} className="video-feed" />
//           {scanResult && <Text className="scan-result">Scanned: {scanResult}</Text>}
//         </div>
//       </Modal>

//       <Modal
//         title="Edit CAPA Report"
//         visible={isEditModalVisible}
//         onCancel={handleEditCancel}
//         footer={null}
//         centered
//         className="edit-modal"
//       >
//         <Form
//           form={editForm}
//           layout="vertical"
//           onFinish={handleEditSubmit}
//         >
//           <Row gutter={[16, 16]}>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 name="finding_observation"
//                 label="Finding / Observation"
//                 rules={[{ required: true, message: 'Please enter finding observation' }]}
//               >
//                 <Input.TextArea
//                   className="custom-textarea"
//                   autoSize={{ minRows: 3, maxRows: 6 }}
//                   placeholder="Enter finding or observation"
//                 />
//               </Form.Item>
//             </Col>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 name="root_cause"
//                 label="Root Cause"
//                 rules={[{ required: true, message: 'Please enter root cause' }]}
//               >
//                 <Input.TextArea
//                   className="custom-textarea"
//                   autoSize={{ minRows: 3, maxRows: 6 }}
//                   placeholder="Enter root cause"
//                 />
//               </Form.Item>
//             </Col>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 name="correction_Immediate"
//                 label="Correction (Immediate)"
//                 rules={[{ required: true, message: 'Please enter correction immediate' }]}
//               >
//                 <Input.TextArea
//                   className="custom-textarea"
//                   autoSize={{ minRows: 3, maxRows: 6 }}
//                   placeholder="Enter immediate correction"
//                 />
//               </Form.Item>
//             </Col>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 name="corrective_action"
//                 label="Corrective Action"
//                 rules={[{ required: true, message: 'Please enter corrective action' }]}
//               >
//                 <Input.TextArea
//                   className="custom-textarea"
//                   autoSize={{ minRows: 3, maxRows: 6 }}
//                   placeholder="Enter corrective action"
//                 />
//               </Form.Item>
//             </Col>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 name="prevention_plan"
//                 label="Prevention Plan"
//                 rules={[{ required: true, message: 'Please enter prevention plan' }]}
//               >
//                 <Input.TextArea
//                   className="custom-textarea"
//                   autoSize={{ minRows: 3, maxRows: 6 }}
//                   placeholder="Enter prevention plan"
//                 />
//               </Form.Item>
//             </Col>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 name="person_responsible"
//                 label="Person Responsible"
//                 rules={[{ required: true, message: 'Please enter person responsible' }]}
//               >
//                 <Input.TextArea
//                   className="custom-textarea"
//                   autoSize={{ minRows: 3, maxRows: 6 }}
//                   placeholder="Enter person responsible"
//                 />
//               </Form.Item>
//             </Col>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 name="due_date"
//                 label="Due Date"
//                 rules={[{ required: true, message: 'Please select due date' }]}
//               >
//                 <DatePicker
//                   selected={editDueDate}
//                   onChange={handleDueDateChange}
//                   dateFormat="yyyy-MM-dd"
//                   placeholderText="Select due date"
//                   className="custom-date-picker"
//                 />
//               </Form.Item>
//             </Col>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 name="status"
//                 label="Status"
//                 rules={[{ required: true, message: 'Please select status' }]}
//               >
//                 <Select placeholder="Select status" className="custom-select">
//                   <Select.Option value="Open">Open</Select.Option>
//                   <Select.Option value="In Progress">In Progress</Select.Option>
//                   <Select.Option value="Closed">Closed</Select.Option>
//                 </Select>
//               </Form.Item>
//             </Col>
//             <Col span={24} style={{ textAlign: 'center' }}>
//               <Form.Item>
//                 <Button type="primary" htmlType="submit" loading={loading} className="submit-button">
//                   Update
//                 </Button>
//                 <Button type="default" onClick={handleEditCancel} style={{ marginLeft: 8 }}>
//                   Cancel
//                 </Button>
//               </Form.Item>
//             </Col>
//           </Row>
//         </Form>
//       </Modal>

//       <style>
//         {`
//           .custom-textarea {
//             border-radius: 6px;
//             border: 1px solid #d9d9d9;
//             font-size: 14px;
//             transition: all 0.3s;
//             resize: none;
//           }
//           .custom-textarea:focus {
//             border-color: #1d39c4;
//             box-shadow: 0 0 0 2px rgba(29, 57, 196, 0.2);
//           }
//           @media (max-width: 768px) {
//             .custom-textarea {
//               font-size: 13px;
//             }
//           }
//           @media (max-width: 576px) {
//             .custom-textarea {
//               font-size: 12px;
//             }
//           }
//           .main-card {
//             border-radius: 8px;
//             box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
//             background: #ffffff;
//             padding: 24px;
//           }
//           .filter-row {
//             margin-bottom: 24px;
//           }
//           .form-card {
//             border-radius: 8px;
//             margin-bottom: 24px;
//             background: #fafafa;
//             box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
//           }
//           .form-card .ant-card-head {
//             background: #1d39c4;
//             color: #ffffff;
//             border-radius: 6px 6px 0 0;
//             font-weight: 500;
//           }
//           .custom-select {
//             border-radius: 6px;
//             border: 1px solid #d9d9d9;
//             height: 40px;
//             font-size: 14px;
//             transition: all 0.3s;
//             background: #ffffff;
//           }
//           .custom-select:focus {
//             border-color: #1d39c4;
//             box-shadow: 0 0 0 2px rgba(29, 57, 196, 0.2);
//           }
//           .custom-date-picker {
//             border-radius: 6px;
//             border: 1px solid #d9d9d9;
//             height: 40px;
//             font-size: 14px;
//             transition: all 0.3s;
//             width: 100%;
//             padding: 8px 12px;
//             box-sizing: border-box;
//           }
//           .custom-date-picker:focus {
//             border-color: #1d39c4;
//             box-shadow: 0 0 0 2px rgba(29, 57, 196, 0.2);
//           }
//           .react-datepicker {
//             font-family: 'Roboto', sans-serif;
//             font-size: 14px;
//             border-radius: 6px;
//           }
//           .react-datepicker__day--selected {
//             background-color: #1d39c4 !important;
//             color: #ffffff !important;
//           }
//           .react-datepicker__day--keyboard-selected {
//             background-color: #597ef7 !important;
//           }
//           .submit-button {
//             border-radius: 6px;
//             padding: 8px 24px;
//             font-size: 14px;
//             background: #1d39c4;
//             border: none;
//             transition: all 0.3s;
//           }
//           .submit-button:hover {
//             background: #597ef7;
//           }
//           .capa-table {
//             border-radius: 8px;
//             overflow: hidden;
//             background: #ffffff;
//             box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
//           }
//           .capa-table .ant-table-thead > tr > th {
//             background: #f0f5ff;
//             color: #1d39c4;
//             font-weight: 500;
//             white-space: nowrap;
//           }
//           .capa-table .ant-table-tbody > tr > td {
//             white-space: nowrap;
//             font-size: 13px;
//           }
//           .action-button {
//             border-radius: 4px;
//             font-size: 12px;
//             padding: 4px 12px;
//           }
//           .edit-button {
//             background: #faad14;
//             border: none;
//           }
//           .edit-button:hover {
//             background: #ffc107;
//           }
//           .review-button {
//             border-radius: 6px;
//             padding: 8px 24px;
//             font-size: 14px;
//             background: #52c41a;
//             border: none;
//             transition: all 0.3s;
//           }
//           .review-button:hover {
//             background: #73d13d;
//           }
//           .stop-button {
//             border-radius: 6px;
//             padding: 8px 24px;
//             font-size: 14px;
//             transition: all 0.3s;
//           }
//           .scan-modal .ant-modal-content,
//           .edit-modal .ant-modal-content {
//             border-radius: 8px;
//           }
//           .video-container {
//             display: flex;
//             flex-direction: column;
//             align-items: center;
//             gap: 16px;
//           }
//           .video-feed {
//             width: 100%;
//             max-width: 400px;
//             height: 300px;
//             border-radius: 6px;
//             border: 1px solid #d9d9d9;
//           }
//           .scan-result {
//             font-size: 14px;
//             color: #1d39c4;
//             font-weight: 500;
//           }
//           @media (max-width: 768px) {
//             .capa-report-container {
//               padding: 16px;
//             }
//             .main-card {
//               padding: 16px;
//             }
//             .custom-select,
//             .custom-date-picker {
//               height: 36px;
//               font-size: 13px;
//             }
//             .submit-button,
//             .review-button,
//             .stop-button {
//               padding: 6px 16px;
//               font-size: 13px;
//             }
//             .capa-table {
//               font-size: 12px;
//             }
//             .capa-table .ant-table-thead > tr > th,
//             .capa-table .ant-table-tbody > tr > td {
//               padding: 8px 6px;
//             }
//             .action-button {
//               padding: 4px 8px;
//               font-size: 11px;
//             }
//             .video-feed {
//               max-width: 300px;
//               height: 200px;
//             }
//           }
//           @media (max-width: 576px) {
//             .capa-report-container {
//               padding: 12px;
//             }
//             .custom-select,
//             .custom-date-picker {
//               height: 32px;
//               font-size: 12px;
//             }
//             .submit-button,
//             .review-button,
//             .stop-button {
//               padding: 4px 12px;
//               font-size: 12px;
//             }
//             .capa-table {
//               font-size: 11px;
//             }
//             .video-feed {
//               max-width: 250px;
//               height: 150px;
//             }
//           }
//         `}
//       </style>
//     </div>
//   );
// };

// export default CapaReportPage;